import React, { useMemo, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faLock,
  faShieldHalved,
  faPaperPlane,
  faChartLine,
  faArrowLeft,
} from '@fortawesome/free-solid-svg-icons';
import { resetPassword } from '../lib/api';
import AppLogo from '../components/AppLogo';
import SiteFooter from '../components/SiteFooter';
import './login.css';

const ResetPassword = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = useMemo(() => searchParams.get('token') || '', [searchParams]);

  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');

  const onSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setMessage('');

    if (!token) {
      setError('Invalid reset link. Please request a new password reset.');
      return;
    }

    if (newPassword.length < 6) {
      setError('Password must be at least 6 characters.');
      return;
    }

    if (newPassword !== confirmPassword) {
      setError('Passwords do not match.');
      return;
    }

    setLoading(true);
    try {
      const res = await resetPassword({ token, newPassword });
      setMessage(res?.message || 'Password reset successfully.');
      setTimeout(() => navigate('/login', { replace: true }), 2000);
    } catch (err) {
      setError(err.message || 'Failed to reset password.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="auth-page">
      <div className="login">
      <div className="login__backdrop login__backdrop--brand" aria-hidden />
      <div className="login__backdrop login__backdrop--form" aria-hidden />

      <aside className="login__brand">
        <AppLogo to="/" variant="brand" />

        <div className="login__meta-badge">
          <img
            src={`${process.env.PUBLIC_URL}/meta.jpeg`}
            alt="Meta Business Partner"
            className="login__meta-badge__img"
          />
        </div>

        <h1 className="login__title">
          Choose a new <span>password</span>
        </h1>
        <p className="login__lead">
          Enter and confirm your new password below. You can sign in immediately after saving.
        </p>

        <ul className="login__features">
          <li>
            <FontAwesomeIcon icon={faPaperPlane} />
            At least 6 characters
          </li>
          <li>
            <FontAwesomeIcon icon={faChartLine} />
            Use a strong, unique password
          </li>
          <li>
            <FontAwesomeIcon icon={faShieldHalved} />
            Secure, Meta-approved platform
          </li>
        </ul>
      </aside>

      <main className="login__form-area">
        <div className="login__card">
          <div className="login__card-head">
            <h2>Set new password</h2>
            <p>Create a new password for your account.</p>
          </div>

          {!token ? (
            <div className="login__alert login__alert--error" role="alert">
              Invalid or missing reset link.{' '}
              <Link to="/forgot-password">Request a new one</Link>.
            </div>
          ) : (
            <form onSubmit={onSubmit} className="login__form">
              <div className="login__field">
                <label htmlFor="new-password">New password</label>
                <div className="login__input-wrap">
                  <FontAwesomeIcon icon={faLock} className="login__input-icon" />
                  <input
                    id="new-password"
                    type="password"
                    className="login__input"
                    placeholder="Enter new password"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    autoComplete="new-password"
                    required
                  />
                </div>
              </div>

              <div className="login__field">
                <label htmlFor="confirm-password">Confirm password</label>
                <div className="login__input-wrap">
                  <FontAwesomeIcon icon={faLock} className="login__input-icon" />
                  <input
                    id="confirm-password"
                    type="password"
                    className="login__input"
                    placeholder="Confirm new password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    autoComplete="new-password"
                    required
                  />
                </div>
              </div>

              {error && (
                <div className="login__alert login__alert--error" role="alert">
                  {error}
                </div>
              )}

              {message && (
                <div className="login__alert login__alert--success" role="status">
                  {message}
                </div>
              )}

              <button className="login__btn login__btn--primary" type="submit" disabled={loading}>
                {loading ? 'Saving…' : 'Reset password'}
              </button>
            </form>
          )}

          <div className="login__help">
            <Link to="/login" className="login__back-link">
              <FontAwesomeIcon icon={faArrowLeft} aria-hidden />
              Back to sign in
            </Link>
          </div>
        </div>

        <p className="login__powered">Powered by Innovative Info Solutions</p>
      </main>
      </div>
      <SiteFooter />
    </div>
  );
};

export default ResetPassword;
